import React from "react"
import { MapContainer, TileLayer, Marker, Circle } from "react-leaflet"
import { ClickHandler, FlyTo, MapResizer } from "./MapUtils"
import { selectedIcon, pulsingIcon } from "./MapIcons"
import { PickedLocation } from "./types"

interface MapViewProps {
  picked: PickedLocation | null
  gpsLocation: PickedLocation | null
  gpsLoading: boolean
  accuracy: number | null
  flyTarget: PickedLocation | null
  onPick: (lat: number, lng: number) => void
}

const DEFAULT_CENTER: [number, number] = [-0.0236, 37.9062]

export const MapView: React.FC<MapViewProps> = ({
  picked, gpsLocation, gpsLoading, accuracy, flyTarget, onPick,
}) => {
  const center: [number, number] = picked
    ? [picked.lat, picked.lng]
    : DEFAULT_CENTER

  const isGPSPin =
    picked && gpsLocation &&
    picked.lat === gpsLocation.lat &&
    picked.lng === gpsLocation.lng

  return (
    <div className="relative" style={{ height: "380px" }}>

      {/* GPS detecting overlay */}
      {gpsLoading && (
        <div
          style={{ position: "absolute", inset: 0, zIndex: 1000 }}
          className="bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center gap-3"
        >
          <div className="relative w-16 h-16">
            <div className="absolute inset-0 rounded-full border-2 border-emerald-400/30 animate-ping" />
            <div className="absolute inset-2 rounded-full border-2 border-emerald-500/40 flex items-center justify-center">
              <div className="w-6 h-6 rounded-full border-2 border-t-emerald-400 border-emerald-500/20 animate-spin" />
            </div>
          </div>
          <div className="text-center">
            <p className="text-white text-sm font-semibold">Detecting location</p>
            <p className="text-neutral-500 text-xs mt-0.5">
              Allow location access if prompted
            </p>
          </div>
        </div>
      )}

      {/* Map */}
      <MapContainer
        key="farm-map"
        center={center}
        zoom={picked ? 15 : 6}
        style={{ height: "100%", width: "100%", zIndex: 1 }}
        zoomControl
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; InspireHub Limited'
        />

        <MapResizer />

        <ClickHandler onPick={onPick} />

        {flyTarget && !gpsLoading && (
          <FlyTo lat={flyTarget.lat} lng={flyTarget.lng} />
        )}

        {/* GPS accuracy radius */}
        {gpsLocation && accuracy && !gpsLoading && (
          <Circle
            center={[gpsLocation.lat, gpsLocation.lng]}
            radius={accuracy}
            pathOptions={{
              color: "#10b981",
              fillColor: "#10b981",
              fillOpacity: 0.08,
              weight: 1,
              dashArray: "4 4",
            }}
          />
        )}

        {/* Pulsing GPS dot */}
        {gpsLocation && !gpsLoading && (
          <Marker
            position={[gpsLocation.lat, gpsLocation.lng]}
            icon={pulsingIcon}
            zIndexOffset={500}
          />
        )}

        {/* Selected pin — only when different from GPS dot */}
        {picked && !isGPSPin && (
          <Marker
            position={[picked.lat, picked.lng]}
            icon={selectedIcon}
          />
        )}
      </MapContainer>

      {/* Hints */}
      {!picked && !gpsLoading && (
        <div
          style={{
            position: "absolute", top: 12,
            left: "50%", transform: "translateX(-50%)",
            zIndex: 500,
          }}
          className="bg-black/70 backdrop-blur-sm border border-white/10 rounded-full px-4 py-1.5 pointer-events-none"
        >
          <span className="text-white text-xs">Click the map to drop a pin</span>
        </div>
      )}

      {picked && !gpsLoading && (
        <div
          style={{
            position: "absolute", bottom: 12,
            left: "50%", transform: "translateX(-50%)",
            zIndex: 500,
          }}
          className="bg-black/70 backdrop-blur-sm border border-emerald-500/30 rounded-full px-4 py-1.5 pointer-events-none"
        >
          <span className="text-emerald-400 text-xs">📍 Click map to reposition</span>
        </div>
      )}
    </div>
  )
}